import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningOrder} from '@domain/planning/planning-order';
import {ConfirmProductionOrderDeletionCommand} from '@presentation/pages/texfab/planning/confirm-production-order-deletion/confirm-production-order-deletion-command';
import {ListOverviewActionsComponent} from '@presentation/pages/texfab/production-schedule/overview/actions/list-overview-actions.component';
import {ListOverviewAction} from '@presentation/pages/texfab/production-schedule/overview/list-overview-action';
import {ProductionScheduleOverviewStatusComponent} from '@presentation/pages/texfab/production-schedule/overview/status/production-schedule-overview-status.component';
import {AssertionUtils, BaseComponent, ColDefBuilderFactoryService, DialogBuilderFactoryService, GridOptionsBuilderFactoryService, TranslateService} from '@vdw/angular-component-library';
import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  GetRowIdFunc,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
  RowDragCallbackParams,
  RowDragEndEvent,
  RowDragLeaveEvent,
  RowDragMoveEvent,
  RowHighlightPosition,
  RowNode,
  ValueGetterParams
} from 'ag-grid-community';
import {cloneDeep, isEmpty, isNil} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {SelectProductionOrderParameters} from './select-production-order/select-production-order-parameters';
import {SelectProductionOrderComponent} from './select-production-order/select-production-order.component';

@Component({
  selector: 'app-production-orders-list',
  templateUrl: './production-orders-list.component.html',
  styleUrls: ['./production-orders-list.component.scss']
})
export class ProductionOrdersListComponent extends BaseComponent implements OnInit {
  @Input() public planningOrders: PlanningOrder[] = [];
  @Input() public machineQualityId: number;
  @Input() public creelId: number;
  @Input() public weaveProductId: number;
  @Input() public tuftProductId: number;
  @Input() public machine: PlanningMachine;
  @Input() public viewOnly: boolean;
  @Output() public deletePlanningOrder: EventEmitter<PlanningOrder> = new EventEmitter<PlanningOrder>();
  @Output() public addPlanningOrders: EventEmitter<PlanningOrder[]> = new EventEmitter<PlanningOrder[]>();
  @Output() public updatePlanningOrders: EventEmitter<PlanningOrder[]> = new EventEmitter<PlanningOrder[]>();
  @Output() public selectPlanningOrder: EventEmitter<PlanningOrder> = new EventEmitter<PlanningOrder>();
  public gridOptions: GridOptions;

  private gridModelForListOfProductionOrders: GridApi;
  private isDraggingProductionOrder = false;

  public constructor(
    private readonly translate: TranslateService,
    private readonly confirmProductionOrderDeletionCommand: ConfirmProductionOrderDeletionCommand,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setGridOptions();
  }

  public addProductionOrders(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog<SelectProductionOrderParameters, PlanningOrder[]>(SelectProductionOrderComponent, {
        machineQualityId: this.machineQualityId,
        creelId: this.creelId,
        machine: this.machine,
        weaveProductId: this.weaveProductId,
        selectedProductionOrders: this.planningOrders.map((planningOrder: PlanningOrder) => planningOrder.productionOrder),
        tuftProductId: this.tuftProductId
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((planningOrders: PlanningOrder[]) => {
        if (!isEmpty(planningOrders)) {
          this.addPlanningOrders.emit(planningOrders);
        }
      });
  }

  public hasPlanningOrders(): boolean {
    return this.planningOrders?.length > 0;
  }

  public onRowDragEnter(): void {
    this.isDraggingProductionOrder = true;
  }

  public onRowDragLeave(event: RowDragLeaveEvent): void {
    event.api.forEachNode((rowNode: RowNode) => {
      rowNode.setHighlighted(null);
    });
  }

  public onRowDragMove({api, overIndex, node, overNode, y}: RowDragMoveEvent<PlanningOrder>): void {
    api.forEachNode((rowNode: RowNode) => {
      rowNode.setHighlighted(null);
    });

    if (node.rowIndex !== overIndex && overNode.data.canBeRescheduled()) {
      const isMouseOnBottomHalfOfRow = y - overNode.rowTop > overNode.rowHeight / 2;

      if ((node.rowIndex - overIndex !== -1 || isMouseOnBottomHalfOfRow) && (node.rowIndex - overIndex !== 1 || !isMouseOnBottomHalfOfRow)) {
        (overNode as RowNode).setHighlighted(isMouseOnBottomHalfOfRow ? RowHighlightPosition.Below : RowHighlightPosition.Above);
      }
    }
  }

  public onRowDragEnd({node, overNode, overIndex}: RowDragEndEvent<PlanningOrder>): void {
    if (node.rowIndex !== overIndex && overNode.data.canBeRescheduled()) {
      const updatedPlanningOrders = cloneDeep(this.planningOrders);

      if (!AssertionUtils.isNullOrUndefined((overNode as RowNode).highlighted)) {
        const planningOrder = node.data;

        const isDraggingUp = node.rowIndex > overIndex && (overNode as RowNode).highlighted === RowHighlightPosition.Below;
        updatedPlanningOrders.splice(node.rowIndex, 1);
        updatedPlanningOrders.splice(isDraggingUp ? overIndex + 1 : overIndex, 0, planningOrder);
        this.updatePlanningOrders.emit(updatedPlanningOrders);
      }
    }

    (overNode as RowNode).setHighlighted(null);
    this.isDraggingProductionOrder = false;
  }

  public onEscapeKeydown(event: KeyboardEvent): void {
    if (this.isDraggingProductionOrder) {
      event.stopPropagation();
    }
  }

  public getRowId: GetRowIdFunc = (params: GetRowIdParams<PlanningOrder>) => `${params.data.id}, ${params.data.productionOrder?.id}`;

  private setGridOptions(): void {
    this.gridOptions = this.gridOptionsBuilderFactoryService
      .getBuilder(this.getColumnDefs(), GridIdentifier.PRODUCTION_ORDERS_LIST)
      .withSuppressRowClickSelection()
      .withSuppressRowHoverHighlight()
      .withLockPinnedColumns()
      .withOnGridReady((params: GridReadyEvent) => this.onGridReadyEventForListOfProductionOrders(params))
      .build();
  }

  private getColumnDefs(): ColDef[] {
    const colDefs: ColDef[] = [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('productionOrder.name')
        .withHeaderName('GENERAL.NAME')
        .withRowDrag((params: RowDragCallbackParams): boolean => {
          const planningOrder: PlanningOrder = params.data;

          return planningOrder.canBeRescheduled() && !this.viewOnly;
        })
        .withOnCellClicked((event: CellClickedEvent): void => {
          const planningOrder: PlanningOrder = event.data;

          if (!isNil(planningOrder.id)) {
            this.selectPlanningOrder.emit(planningOrder);
          }
        })
        .withCellClass((params: CellClassParams): string => {
          const planningOrder: PlanningOrder = params.data;

          return isNil(planningOrder.id) ? '' : 'url';
        })
        .withSuppressMovable()
        .withLockVisible()
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('productionOrder.status')
        .withHeaderName('GENERAL.STATUS')
        .withCellClass('center')
        .withTooltipValueGetter((params: ITooltipParams): any => this.translate.instant(`PRODUCTION_ORDER.STATUS.${params.value}`))
        .withCellRenderer(ProductionScheduleOverviewStatusComponent)
        .withSortable(false)
        .withoutFilter()
        .build()
    ];

    if (!this.viewOnly) {
      colDefs.splice(
        1,
        0,
        this.colDefBuilderFactoryService.getBuilder().withField('productionOrder.dueDate').withHeaderName('GENERAL.DATETIME.DUE_DATE').withDate().withSortable(false).withoutFilter().build()
      );

      colDefs.push(
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('GENERAL.ACTIONS.ACTIONS')
          .withoutHeaderName()
          .withCellRenderer(ListOverviewActionsComponent)
          .withValueGetter((params: ValueGetterParams) => {
            const actions: ListOverviewAction[] = [];
            const planningOrder: PlanningOrder = params.data;

            if (planningOrder.canBeRescheduled()) {
              actions.push({
                name: 'PLANNING.ACTIONS.REMOVE',
                actionFn: () => this.confirmPlanningOrderDeletion(planningOrder)
              });
            }

            return actions;
          })
          .withSuppressMovable('right')
          .withLockVisible()
          .withSortable(false)
          .withoutFilter()
          .build()
      );
    }

    return colDefs;
  }

  private confirmPlanningOrderDeletion(planningOrder: PlanningOrder): void {
    const title = this.translate.instant('PLANNING.REMOVE_PRODUCTION_ORDER');
    const message = this.translate.instant('PLANNING.CONFIRM_PRODUCTION_ORDER_REMOVAL', {productionOrderName: planningOrder.productionOrder.name});

    this.confirmProductionOrderDeletionCommand
      .execute({title, message})
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.deletePlanningOrder.emit(planningOrder);
      });
  }

  private onGridReadyEventForListOfProductionOrders(params: GridReadyEvent): void {
    this.gridModelForListOfProductionOrders = params.api;
    this.gridModelForListOfProductionOrders.sizeColumnsToFit();
  }
}
